import '~/entrypoints/config/init';

import { createApp, h } from 'vue';
import VueGoogleMaps from '~/google_maps';
import { i18n } from '~/zone_inertia/i18n';
import { createInertiaApp, Link } from '@inertiajs/vue3';

import HubApp from '../plugins/hub_app';

// Eagerly load the pages so that Vite can analyse the dynamic imports.
// We use eager loading here because for now we want to avoid having
// to load as many chunks as possible in the browser.
//
// This will essentially create an object with the following structure:
// ```
// import * as __glob_0_0 from './WorkOrderes/Index.vue';
// import * as __glob_0_1 from './WorkOrderes/Show.vue';
// const PAGES_MAP = {
//   '../pages/WorkOrders/Index.vue': __glob_0_0,
//   '../pages/WorkOrders/Show.vue': __glob_0_1,
// }
// ```
const PAGES_MAP = import.meta.glob(['../pages/**/*.vue'], { eager: true });

// The actual setup from the documentation doesn't work
// and we have to use this work around in our setup.
// See:
// - [Client setup](https://inertiajs.com/client-side-setup)
// - [Server setup](https://inertiajs.com/server-side-setup)
//
// Do not fail if we're not using Inertia in "this page"
if (document.querySelector('#app')) {
  // On a back/forward navigation Inertia restores the page from `window.history.state`
  // instead of `#app[data-page]`, and it only checks that the state is truthy — a state
  // belonging to some other library resolves to `component: undefined`. `config/init`
  // restores Inertia's own state after Turbo clobbers it, but if anything else has
  // claimed this entry, drop the state so Inertia falls back to `data-page`.
  if (window.history.state && !window.history.state.component) {
    window.history.replaceState(null, '', window.location.href);
  }

  createInertiaApp({
    resolve: (pagePath) => {
      const page = PAGES_MAP[`../pages/${pagePath}.vue`];

      if (!page) {
        throw new Error(`Page not found: ${page}`);
      }

      return typeof page === 'function' ? page() : page;
    },
    setup({ el, App, props, plugin }) {
      const vueApp = createApp({
        name: 'InertiaRoot',
        render: () => h(App, props),
      });

      // Add the `App[Component]`s, directives & filters
      vueApp.use(HubApp);
      vueApp.use(plugin);
      vueApp.use(i18n);

      // Add the Intertiajs `Link` as a global component
      // so that we improve DX ergonomics.
      vueApp.component('InertiaLink', Link);

      // Initialize Google Maps if needed
      const {
        initialPage: { props: pageProps },
      } = props;

      if (pageProps.googleMapsKey || pageProps.google_maps_key) {
        vueApp.use(VueGoogleMaps, pageProps.googleMapsKey || pageProps.google_maps_key);
      }

      // Vue 3: Handle teleport cleanup via global mixin
      vueApp.mixin({
        beforeUnmount() {
          // Clean up any teleported elements
          const teleportTarget = document.getElementById('teleport-target');

          if (teleportTarget) {
            teleportTarget.innerHTML = '';
          }
        }
      });

      vueApp.mount(el);
    },
  });
}
